import PropTypes from "prop-types";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
// @mui
import { styled } from "@mui/material/styles";
import { Box, Stack, Drawer } from "@mui/material";
// hooks
import useAuth from "../../../hooks/useAuth";
import useResponsive from "../../../hooks/useResponsive";
import useCollapseDrawer from "../../../hooks/useCollapseDrawer";
// utils
import cssStyles from "../../../utils/cssStyles";
// config
import { NAVBAR } from "../../../config";
// components
import Logo from "../../../components/Logo";
import Scrollbar from "../../../components/Scrollbar";
import { NavSectionVertical } from "../../../components/nav-section";
//
import PartnerNavConfig from "./PartnerNavConfig";
import CollapseButton from "./CollapseButton";
// motion
import { m } from "framer-motion";

// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

const sidebarPaperSx = {
  width: NAVBAR.DASHBOARD_WIDTH,
  bgcolor: "#0C2D3C",
  color: "#FFFFFF",
  boxShadow: "0 28px 60px rgba(10, 15, 45, 0.22)",
  p: { lg: 2, xs: 1.5 },
};

const sidebarShellSx = {
  height: "100%",
  overflow: "hidden",
};

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();

  const isDesktop = useResponsive("up", "lg");

  const { user } = useAuth();

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  } = useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Box sx={sidebarShellSx}>
      <Scrollbar
        sx={{
          height: 1,
          "& .simplebar-content": {
            minHeight: 1,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Stack
          spacing={4}
          sx={{
            pb: 2,
            px: isCollapse ? 1.5 : 2.5,
            flexShrink: 0,
            ...(isCollapse && { alignItems: "center" }),
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ minHeight: 72 }}
          >
            {isCollapse ? (
              <Logo
                mini
                imagePath="/static/assets/brand/logo.png"
                width={38}
                height={52}
              />
            ) : (
              <Box
                component={m.div}
                initial={{ opacity: 0, x: -12, scale: 0.96 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{
                  duration: 0.5,
                  ease: "easeOut",
                }}
              >
                <Logo
                  mini={false}
                  imagePath="/static/assets/brand/logo1.png"
                  width={188}
                  height={55}
                />
              </Box>
            )}

            {/* {isDesktop && !isCollapse && (
              <CollapseButton
                onToggleCollapse={onToggleCollapse}
                collapseClick={collapseClick}
              />
            )} */}
          </Stack>
        </Stack>

        <NavSectionVertical
          permissions_list={user?.user_permissions_list}
          navConfig={PartnerNavConfig()}
          isCollapse={isCollapse}
        />
      </Scrollbar>
    </Box>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: "absolute",
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: sidebarPaperSx }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: (theme) => ({
              ...sidebarPaperSx,
              transition: theme.transitions.create("width", {
                duration: theme.transitions.duration.standard,
              }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur({ color: "#0C2D3C", opacity: 0.92 }),
              }),
            }),
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
